import React, {useEffect, useState} from 'react';
import {Button, Card, Col, Divider, Form, Input, InputNumber, Row, Select, Space, Spin, Upload} from 'antd';
import {LoadingOutlined, SaveOutlined} from '@ant-design/icons';
import AdminLayout from "@/components/layout/admin/adminLayout";
import {URLS} from "@/lib/urls";
import {useApiGetAdminProduct, useApiUpdateAdminProduct} from "@/api/product";
import ApiHandleSuccessMessage, {ApiHandleErrorMessage} from "@/lib/helper/apiHandleResultMessage";
import UploadCMP from "@/components/upload";
import TaggableSelect from "@/components/select/apiSelect";
import toFaNumber from "@/lib/utils/number";
import {useApiSearchAdminTopics} from "@/api/topic";
import {useNavigate, useParams} from "react-router-dom";
import FileViewer from "@/lib/helper/FileViewer";
import RenderIf from "@/components/render-if";
import {Trash2} from "lucide-react";

const { TextArea } = Input;
const { Dragger } = Upload;

const ProductStatusEnum = {
    DRAFT: 'draft',
    PUBLISHED: 'published',
    ARCHIVED: 'archived'
};

const ProductGenderEnum = {
    MALE: 'male',
    FEMALE: 'female',
    ALL: 'all'
};

const ProductDeliverToEnum = {
    USER: 'user',
    TEACHER: 'teacher',
    ALL: 'all'
};


function AdminProductEdit(props) {
    const [form] = Form.useForm();
    const { id } = useParams();
    const navigate = useNavigate();

    const [file, setFile] = useState();
    const [topics, setTopics] = useState([]);
    const [selectedTopics, setSelectedTopics] = useState([]);
    const [initialLoading, setInitialLoading] = useState(true);


    const {data: productData, isPending: isPendingGet, refetch} = useApiGetAdminProduct({id: id});


    const {mutate: editProduct, isPending: isPendingEdit} = useApiUpdateAdminProduct();


    useEffect(() => {
        if (productData?.data?.data) {
            const product = productData?.data?.data;


            form.setFieldsValue({
                title: product.title,
                description: product.description,
                status: product.status,
                gender: product.gender,
                buy_price: product.buy_price,
                deliver_to: product.deliver_to,
                deliver_text: product.deliver_text,
                price: product.price,
                topic_ids: product.topics?.map(t => t.id) || [],
            });


            if (product.topics) {
                setTopics(product.topics.map(t => t.id));
            }


            if (product.file_id) {
                setFile({
                    file: {
                        response: {
                            data: {
                                id: product.file_id,
                                url: product.file_url,
                                name: product.file_name
                            }
                        }
                    }
                });
            }

            setInitialLoading(false);
        }
    }, [id, productData, form]);

    const handleSubmit = async (values) => {
        const submitData = {
            ...values,
            file_id: file?.file?.response?.data?.id ?? null,
            topic_ids: topics,
        };

        editProduct({
            id: id,
            ...submitData
        }, {
            onSuccess: (r) => {
                ApiHandleSuccessMessage(r);
                refetch()
                setFile(null)

            },
            onError: (e) => {
                ApiHandleErrorMessage(e);
            }
        });
    };


    const handleRemoveFile = () => {
        setFile(null);
        form.setFieldsValue({ file_id: null });
    };

    const statusOptions = [
        { label: 'پیش‌نویس', value: ProductStatusEnum.DRAFT },
        { label: 'منتشر شده', value: ProductStatusEnum.PUBLISHED },
        { label: 'بایگانی شده', value: ProductStatusEnum.ARCHIVED },
    ];

    const genderOptions = [
        { label: 'مردانه', value: ProductGenderEnum.MALE },
        { label: 'زنانه', value: ProductGenderEnum.FEMALE },
        { label: 'هردو', value: ProductGenderEnum.ALL },
    ];



    const deliverToOptions = [
        { label: 'خود دانش آموز', value: ProductDeliverToEnum.USER },
        { label: 'مربی', value: ProductDeliverToEnum.TEACHER },
        { label: 'هردو', value: ProductDeliverToEnum.ALL },
    ];


    if (initialLoading || isPendingGet) {
        return (
            <AdminLayout pageTitle={'ویرایش محصول'} backTo={URLS.admin.products.list}>
                <Card className="shadow-lg rounded-xl">
                    <div className="flex justify-center items-center h-64">
                        <Spin
                            indicator={<LoadingOutlined style={{ fontSize: 48 }} spin />}
                            tip="در حال بارگذاری اطلاعات..."
                        />
                    </div>
                </Card>
            </AdminLayout>
        );
    }

    return (
        <AdminLayout
            pageTitle={` ویرایش محصول: ${productData?.data?.data?.id || ''}`}
            backTo={URLS.admin.products.list}
        >
            <Card className="shadow-lg rounded-xl">
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleSubmit}
                    className="space-y-4"
                    initialValues={{
                        status: ProductStatusEnum.DRAFT,
                        gender: ProductGenderEnum.ALL,
                    }}
                >
                    <Row gutter={24}>
                        {/* عنوان محصول */}
                        <Col xs={24} md={8}>
                            <Form.Item
                                name="title"
                                label="عنوان محصول"
                                rules={[
                                    { required: false },
                                    { type: 'string', message: 'عنوان باید متن باشد' },
                                ]}
                                className="mb-4"
                            >
                                <Input
                                    placeholder="عنوان محصول را وارد کنید"
                                    size="medium"
                                    className="rounded-lg"
                                />
                            </Form.Item>
                        </Col>

                        <Col xs={24} md={8}>
                            <Form.Item
                                name="deliver_to"
                                label="تحویل به"
                                rules={[
                                    { required: false },
                                    { type: 'string', message: 'وضعیت باید متن باشد' },
                                ]}
                                className="mb-4"
                            >
                                <Select
                                    options={deliverToOptions}
                                    placeholder="دریافت کننده  را انتخاب کنید"
                                    size="medium"
                                    className="rounded-lg"
                                />
                            </Form.Item>
                        </Col>

                        {/* وضعیت */}
                        <Col xs={24} md={8}>
                            <Form.Item
                                name="status"
                                label="وضعیت"
                                rules={[
                                    { required: false },
                                    { type: 'string', message: 'وضعیت باید متن باشد' },
                                ]}
                                className="mb-4"
                            >
                                <Select
                                    options={statusOptions}
                                    placeholder="وضعیت را انتخاب کنید"
                                    size="medium"
                                    className="rounded-lg"
                                />
                            </Form.Item>
                        </Col>
                    </Row>


                    {/* توضیحات */}
                    <Form.Item
                        name="description"
                        label="توضیحات"
                        rules={[
                            { required: false },
                            { type: 'string', message: 'توضیحات باید متن باشد' },
                        ]}
                        className="mb-4"
                    >
                        <Input.TextArea
                            rows={4}
                            placeholder="توضیحات محصول را وارد کنید"
                            className="rounded-lg"
                        />
                    </Form.Item>
                    <Form.Item
                        name="deliver_text"
                        label="محل تحویل"
                        rules={[
                            { required: false },
                            { type: 'string', message: 'محل تحویل باید متن باشد' },
                        ]}
                        className="mb-4"
                    >
                        <Input.TextArea
                            rows={4}
                            placeholder="این متن در پایین برگه تحویل قرار خواهد گرفت ..."
                            className="rounded-lg"
                        />
                    </Form.Item>

                    <Row gutter={24}>
                        {/* قیمت خرید */}
                        <Col xs={24} md={12}>
                            <Form.Item
                                name="buy_price"
                                label="قیمت خرید"
                                rules={[
                                    { required: false },
                                    { type: 'integer', message: 'قیمت خرید باید عدد صحیح باشد' },

                                ]}
                                className="mb-4"
                            >
                                <InputNumber
                                    className="w-full rounded-lg"
                                    size="medium"
                                    placeholder="قیمت خرید به تومان"
                                    min={0}
                                    step={1000}
                                    suffix={'تومان'}
                                    formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                                    parser={value => value.replace(/\$\s?|(,*)/g, '')}
                                />
                            </Form.Item>
                        </Col>

                        {/* قیمت فروش */}
                        <Col xs={24} md={12}>
                            <Form.Item
                                name="price"
                                label="قیمت فروش"
                                rules={[

                                    { type: 'integer', message: 'قیمت فروش باید عدد صحیح باشد' },

                                ]}
                                className="mb-4"
                            >
                                <InputNumber
                                    className="w-full rounded-lg"
                                    size="medium"
                                    placeholder="قیمت فروش به تومان"
                                    min={0}
                                    step={1000}
                                    suffix={'تومان'}
                                    formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                                    parser={value => value.replace(/\$\s?|(,*)/g, '')}
                                />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={24}>
                        {/* جنسیت */}
                        <Col xs={24} md={12}>
                            <Form.Item
                                name="gender"
                                label="جنسیت"
                                rules={[
                                    { required: false },
                                    { type: 'string', message: 'جنسیت باید متن باشد' },
                                ]}
                                className="mb-4"
                            >
                                <Select
                                    options={genderOptions}
                                    placeholder="جنسیت را انتخاب کنید"
                                    size="medium"
                                    className="rounded-lg"
                                />
                            </Form.Item>
                        </Col>

                        <Col xs={24} md={12}>
                            <Form.Item
                                name="topic_ids"

                                rules={[
                                    { required: false },
                                    { type: 'array', message: 'تاپیک‌ها باید آرایه باشند' },
                                ]}
                                className="mb-4"
                            >
                                <TaggableSelect
                                    name={'grade-search'}
                                    className={'mt-2'}
                                    label={'فعال برای مقاطع تحصیلی'}
                                    searchFn={useApiSearchAdminTopics}
                                    onChange={setTopics}
                                    values={topics}
                                    render={(item) => {
                                        return toFaNumber(item.id) + ' - ' + item.title
                                    }}
                                    initialValues={topics}
                                />
                            </Form.Item>
                        </Col>
                    </Row>

                    {/* آپلود فایل */}
                    <Form.Item

                        label="فایل محصول"
                        rules={[
                            { required: false },

                        ]}
                        className="mb-4"
                    >
                        <div className="flex flex-wrap gap-4">
                            <div><UploadCMP
                                dragMode={false}
                                afterUploadFn={setFile}

                            /></div>
                            <div>

                                <RenderIf condition={file && productData?.data?.data?.file?.url}>
                                    <div className={'w-32 h-32 flex flex-col items-center'}>

                                        <FileViewer fileUrl={productData?.data?.data?.file?.url} />
                                        <Button
                                            size="small"

                                            type='link'
                                            danger
                                            onClick={handleRemoveFile}
                                        >
                                            <Trash2 className={'h-4'} />
                                        </Button>
                                    </div>

                                </RenderIf>

                            </div>
                        </div>

                    </Form.Item>


                    <Divider />

                    {/* دکمه‌های عملیات */}
                    <Form.Item className="mb-0">
                        <Space className="w-full justify-between" size="middle">
                            <Button
                                onClick={() => navigate(URLS.admin.products.list)}

                            >
                                انصراف
                            </Button>

                            <Button
                                type="primary"
                                htmlType="submit"
                                loading={isPendingEdit}
                                icon={<SaveOutlined />}

                                size="medium"
                            >
                                {isPendingEdit ? 'در حال ویرایش...' : 'ویرایش محصول'}
                            </Button>
                        </Space>
                    </Form.Item>
                </Form>
            </Card>
        </AdminLayout>
    );
}

export default AdminProductEdit;